// Meta tags for global properties
export enum GlobalMetaTagName {
  CSRFToken = 'csrf-token',
  Features = 'features',
  Experiments = 'experiments',
  BrowserId = 'browser_id',
  CDN = 'cdn',
  GlobalConfig = 'global-config',
  Environment = 'environment',
  ManagedLanguages = 'managed-languages',
  Categories = 'categories',
  Tags = 'tags',
  SentryDSN = 'sentry-dsn',
}

// Meta tags for store initialization
export enum StateMetaTagName {
  Country = 'state.country',
  Currency = 'state.currency',
  CurrenciesRates = 'state.currencies_rates',
  User = 'state.user',
  Retailer = 'state.retailer',
  Brand = 'state.brand',
  Stripe = 'state.stripe',
  ShowRequestedInformationForm = 'state.show_requested_information_form',
}

// Meta tags rendered dynamically per page
export enum DynamicMetaTagName {
  BrandCatalogues = 'brand-catalogues',
  BrandCatalogueTiles = 'brand-catalogue-tiles',
  ShopBrands = 'state.shop.brands',
  // Server verdict: the request's email_verification_token proved a verified
  // email. Stamped ONLY by the server-gated registration wizard/landing blades
  // (auth.register-page, auth.brand-register-page); absence IS the unverified
  // signal — readers must default to false (fail-closed).
  RegistrationEmailVerified = 'state.registration_email_verified',
}

export enum DataSource {
  MetaTag = 'meta_tag',
  API = 'api',
  BuildTime = 'build_time',
}

export type MetaTagName = GlobalMetaTagName | StateMetaTagName | DynamicMetaTagName;

function createDefaultConfig<T>(defaultValue: T): Record<MetaTagName, T> {
  const allTags = [...Object.values(GlobalMetaTagName), ...Object.values(StateMetaTagName), ...Object.values(DynamicMetaTagName)];
  return Object.fromEntries(allTags.map((tag) => [tag, defaultValue])) as Record<MetaTagName, T>;
}

// All states default to MetaTag; override individual entries to migrate to API/BuildTime
export const StateSourceConfig = createDefaultConfig(DataSource.MetaTag);
StateSourceConfig[GlobalMetaTagName.Features] = DataSource.API;
StateSourceConfig[GlobalMetaTagName.Experiments] = DataSource.API;
StateSourceConfig[GlobalMetaTagName.ManagedLanguages] = DataSource.API;
StateSourceConfig[GlobalMetaTagName.GlobalConfig] = DataSource.API;
StateSourceConfig[GlobalMetaTagName.BrowserId] = DataSource.API;
StateSourceConfig[StateMetaTagName.Country] = DataSource.API;
StateSourceConfig[StateMetaTagName.Currency] = DataSource.API;
StateSourceConfig[StateMetaTagName.CurrenciesRates] = DataSource.API;
StateSourceConfig[StateMetaTagName.Retailer] = DataSource.API;
StateSourceConfig[StateMetaTagName.Stripe] = DataSource.API;
StateSourceConfig[StateMetaTagName.User] = DataSource.API;
StateSourceConfig[GlobalMetaTagName.CDN] = DataSource.API;
StateSourceConfig[StateMetaTagName.ShowRequestedInformationForm] = DataSource.API;
StateSourceConfig[GlobalMetaTagName.CSRFToken] = DataSource.API;
StateSourceConfig[GlobalMetaTagName.Categories] = DataSource.API;
StateSourceConfig[GlobalMetaTagName.Tags] = DataSource.API;
StateSourceConfig[GlobalMetaTagName.SentryDSN] = DataSource.API;
StateSourceConfig[GlobalMetaTagName.Environment] = DataSource.API;

// true = base64-encoded JSON, false = plain text
export const InitialStateConfig = createDefaultConfig(true);
InitialStateConfig[GlobalMetaTagName.CSRFToken] = false;
InitialStateConfig[GlobalMetaTagName.CDN] = false;
InitialStateConfig[GlobalMetaTagName.Environment] = false;
InitialStateConfig[GlobalMetaTagName.SentryDSN] = false;
